"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { ToggleRow } from "@/components/ToggleRow";
import { countryLabel } from "@/lib/geo/country-label";

/**
 * The board's Location panel: Remote, then Region → Country → City columns.
 *
 * Every pick widens the search (a region, a country and a city together mean
 * "any of these"), and the most specific pick wins: choosing Germany drops a
 * selected Europe, choosing Munich drops a selected Germany — so the selection
 * never holds a place twice. Picking a region also scopes the Country and City
 * columns to it for browsing. Counts arrive from /api/location-facets when the
 * panel opens (there are ~10k places; they never ship in the page), and a
 * search box reaches every city while only the top rows are ever in the DOM.
 */

interface Data {
  remote: number;
  regions: { value: string; count: number }[];
  countries: { iso: string; count: number; region: string | null }[];
  cities: { key: string; count: number }[];
}

export interface LocationSelection {
  regions: string[];
  countries: string[];
  places: string[];
  remote: boolean;
}

const CITY_ROWS = 100;
const LOCATION_PARAMS = ["region", "country", "city", "workplace", "remote", "page"];

const pending = new Map<string, Promise<Data>>();
function load(qs: string): Promise<Data> {
  let p = pending.get(qs);
  if (!p) {
    p = fetch(`/api/location-facets?${qs}`).then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json() as Promise<Data>;
    });
    p.catch(() => pending.delete(qs));
    pending.set(qs, p);
  }
  return p;
}

const cityName = (key: string) => key.slice(3);
const fold = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Rank matches: name starts with the query first, then contains it. */
function search<T>(items: T[], q: string, label: (t: T) => string): T[] {
  const needle = fold(q);
  const starts: T[] = [];
  const contains: T[] = [];
  for (const it of items) {
    const l = fold(label(it));
    if (l.startsWith(needle)) starts.push(it);
    else if (l.includes(needle)) contains.push(it);
  }
  return [...starts, ...contains];
}

export function LocationPicker({
  regionOrder,
  selected,
}: {
  regionOrder: readonly string[];
  selected: LocationSelection;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const sp = useSearchParams();
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState(false);
  const [countryQ, setCountryQ] = useState("");
  const [cityQ, setCityQ] = useState("");
  const regionOfIso = useMemo(
    () => new Map((data?.countries ?? []).map((c) => [c.iso, c.region])),
    [data],
  );
  const [focus, setFocus] = useState<string | null>(selected.regions[0] ?? null);

  // Counts follow every filter except page/sort; location picks are skipped
  // server-side, so they stay put while the user ticks places.
  const qs = useMemo(() => {
    const p = new URLSearchParams(sp);
    p.delete("page");
    p.delete("sort");
    return p.toString();
  }, [sp]);

  useEffect(() => {
    let live = true;
    setFailed(false);
    load(qs).then(
      (d) => live && setData(d),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [qs]);

  // Focus follows the first selected country's region when none was chosen.
  useEffect(() => {
    if (focus || !data) return;
    const iso = selected.countries[0] ?? selected.places[0]?.slice(0, 2);
    const r = iso ? regionOfIso.get(iso) : null;
    if (r) setFocus(r);
  }, [data, focus, regionOfIso, selected]);

  const href = (next: Partial<LocationSelection>) => {
    const s = { ...selected, ...next };
    const p = new URLSearchParams(sp);
    for (const k of LOCATION_PARAMS) {
      if (k === "workplace" && sp.get("workplace") === "onsite" && next.remote === undefined) continue;
      p.delete(k);
    }
    for (const v of s.regions) p.append("region", v);
    for (const v of s.countries) p.append("country", v);
    for (const v of s.places) p.append("city", v);
    if (s.remote) p.set("workplace", "remote");
    const q = p.toString();
    return q ? `${pathname}?${q}` : pathname;
  };

  const toggleRegion = (r: string) =>
    selected.regions.includes(r)
      ? href({ regions: selected.regions.filter((v) => v !== r) })
      : href({
          regions: [...selected.regions, r],
          // Anything inside the region is now covered by it.
          countries: selected.countries.filter((c) => regionOfIso.get(c) !== r),
          places: selected.places.filter((k) => regionOfIso.get(k.slice(0, 2)) !== r),
        });
  const toggleCountry = (iso: string) =>
    selected.countries.includes(iso)
      ? href({ countries: selected.countries.filter((v) => v !== iso) })
      : href({
          countries: [...selected.countries, iso],
          regions: selected.regions.filter((r) => r !== regionOfIso.get(iso)),
          places: selected.places.filter((k) => !k.startsWith(`${iso}:`)),
        });
  const toggleCity = (key: string) => {
    const iso = key.slice(0, 2);
    return selected.places.includes(key)
      ? href({ places: selected.places.filter((v) => v !== key) })
      : href({
          places: [...selected.places, key],
          countries: selected.countries.filter((c) => c !== iso),
          regions: selected.regions.filter((r) => r !== regionOfIso.get(iso)),
        });
  };

  const regionCount = new Map(data?.regions.map((r) => [r.value, r.count]));

  const countries = useMemo(() => {
    const all = data?.countries ?? [];
    const pool = countryQ
      ? search(all, countryQ, (c) => countryLabel(c.iso))
      : focus
        ? all.filter((c) => c.region === focus)
        : all;
    const picked = selected.countries.map(
      (iso) => all.find((c) => c.iso === iso) ?? { iso, count: 0, region: null },
    );
    return [...picked, ...pool.filter((c) => !selected.countries.includes(c.iso))];
  }, [data, countryQ, focus, selected.countries]);

  const { cities, cityTotal } = useMemo(() => {
    const all = data?.cities ?? [];
    let pool = all;
    if (cityQ) pool = search(all, cityQ, (c) => cityName(c.key));
    else if (selected.countries.length)
      pool = all.filter((c) => selected.countries.includes(c.key.slice(0, 2)));
    else if (focus) pool = all.filter((c) => regionOfIso.get(c.key.slice(0, 2)) === focus);
    const picked = selected.places.map(
      (key) => all.find((c) => c.key === key) ?? { key, count: 0 },
    );
    const rest = pool.filter((c) => !selected.places.includes(c.key));
    return { cities: [...picked, ...rest.slice(0, CITY_ROWS)], cityTotal: rest.length };
  }, [data, cityQ, focus, regionOfIso, selected.countries, selected.places]);

  const anySelected =
    selected.regions.length + selected.countries.length + selected.places.length > 0 ||
    selected.remote;

  const scopeLabel = cityQ
    ? null
    : selected.countries.length === 1
      ? countryLabel(selected.countries[0]!)
      : selected.countries.length > 1
        ? "selected countries"
        : focus;

  return (
    <div className="text-sm">
      <div className="flex items-center gap-3 border-b border-neutral-100 px-2 py-2">
        {/* 16rem = the Region column's row width, so the counts line up. */}
        <ul className="w-full min-w-0 sm:w-64">
          <ToggleRow
            label="Remote"
            count={data?.remote}
            selected={selected.remote}
            href={href({ remote: !selected.remote })}
          />
        </ul>
        <span className="hidden min-w-0 flex-1 truncate text-xs text-neutral-400 sm:block">
          Pick any mix of regions, countries and cities.
        </span>
        {anySelected ? (
          <a
            href={href({ regions: [], countries: [], places: [], remote: false })}
            onClick={(e) => {
              e.preventDefault();
              router.push(href({ regions: [], countries: [], places: [], remote: false }), { scroll: false });
            }}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-neutral-100 hover:text-brand-800"
          >
            Clear location
          </a>
        ) : null}
      </div>

      <div className="grid md:grid-cols-[17rem_minmax(0,1fr)_minmax(0,1fr)] md:divide-x md:divide-neutral-100">
        <Column title="Region">
          <ul className="space-y-px">
            <li>
              <button
                type="button"
                onClick={() => setFocus(null)}
                className={`w-full rounded-md px-1.5 py-1 text-left text-sm hover:bg-neutral-100 ${
                  focus ? "text-neutral-500" : "font-medium text-ink"
                }`}
              >
                All regions
              </button>
            </li>
            {regionOrder.map((r) => (
              <ToggleRow
                key={r}
                label={r}
                count={data ? (regionCount.get(r) ?? 0) : undefined}
                selected={selected.regions.includes(r)}
                href={toggleRegion(r)}
                onClick={() => setFocus(r)}
                accent={focus === r}
              />
            ))}
          </ul>
        </Column>

        <Column
          title="Country"
          scope={!countryQ && focus ? focus : null}
          onClearScope={() => setFocus(null)}
          query={countryQ}
          onQuery={setCountryQ}
          placeholder="Search countries"
          onEnter={() => {
            const c = countries.find((x) => !selected.countries.includes(x.iso));
            if (c) router.push(toggleCountry(c.iso), { scroll: false });
          }}
        >
          {!data ? (
            <Status failed={failed} />
          ) : countries.length === 0 ? (
            <Empty>No countries match “{countryQ}”.</Empty>
          ) : (
            <ul className="space-y-px">
              {countries.map((c) => (
                <ToggleRow
                  key={c.iso}
                  label={countryLabel(c.iso)}
                  count={c.count}
                  selected={selected.countries.includes(c.iso)}
                  href={toggleCountry(c.iso)}
                />
              ))}
            </ul>
          )}
        </Column>

        <Column
          title="City"
          scope={scopeLabel}
          onClearScope={focus && !selected.countries.length ? () => setFocus(null) : undefined}
          query={cityQ}
          onQuery={setCityQ}
          placeholder="Search all cities"
          onEnter={() => {
            const c = cities.find((x) => !selected.places.includes(x.key));
            if (c) router.push(toggleCity(c.key), { scroll: false });
          }}
        >
          {!data ? (
            <Status failed={failed} />
          ) : cities.length === 0 ? (
            <Empty>{cityQ ? `No cities match “${cityQ}”.` : "No cities here yet."}</Empty>
          ) : (
            <>
              <ul className="space-y-px">
                {cities.map((c) => (
                  <ToggleRow
                    key={c.key}
                    label={cityName(c.key)}
                    title={`${cityName(c.key)}, ${countryLabel(c.key.slice(0, 2))}`}
                    hint={c.key.slice(0, 2)}
                    count={c.count}
                    selected={selected.places.includes(c.key)}
                    href={toggleCity(c.key)}
                  />
                ))}
              </ul>
              {cityTotal > CITY_ROWS ? (
                <p className="px-1.5 pb-1 pt-2 text-xs text-neutral-400">
                  Top {CITY_ROWS} of {cityTotal.toLocaleString("en-US")} — search to find the rest.
                </p>
              ) : null}
            </>
          )}
        </Column>
      </div>
    </div>
  );
}

function Column({
  title,
  scope,
  onClearScope,
  query,
  onQuery,
  placeholder,
  onEnter,
  children,
}: {
  title: string;
  scope?: string | null;
  onClearScope?: () => void;
  query?: string;
  onQuery?: (q: string) => void;
  placeholder?: string;
  onEnter?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="flex min-w-0 flex-col border-t border-neutral-100 px-2 pt-2.5 first:border-t-0 md:border-t-0">
      <h3 className="flex h-4 items-center gap-1.5 px-1.5 text-[9px]">
        <span className="telex text-neutral-400">{title}</span>
        {scope ? (
          <span className="flex min-w-0 items-center gap-1 text-[11px] font-medium text-neutral-500">
            <span className="text-neutral-300">·</span>
            <span className="truncate">in {scope}</span>
            {onClearScope ? (
              <button
                type="button"
                onClick={onClearScope}
                aria-label={`Show all, not only ${scope}`}
                className="rounded px-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-ink"
              >
                ×
              </button>
            ) : null}
          </span>
        ) : null}
      </h3>
      {onQuery ? (
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onEnter?.();
            }
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          className="mt-2 h-8 w-full rounded-md border border-neutral-300 bg-white px-2.5 text-[13px] text-ink placeholder:text-neutral-400 focus:border-ink focus:outline-none"
        />
      ) : null}
      <div className="mt-1.5 max-h-56 overflow-y-auto overscroll-contain pb-2 md:h-72 md:max-h-none">
        {children}
      </div>
    </section>
  );
}

function Status({ failed }: { failed: boolean }) {
  if (failed) return <Empty>Couldn’t load places — close and reopen to retry.</Empty>;
  return (
    <ul aria-busy className="space-y-px" aria-label="Loading">
      {[72, 56, 64, 48, 60, 52].map((w, i) => (
        <li key={i} className="flex items-center gap-2 px-1.5 py-1">
          <span className="h-4 w-4 rounded border border-neutral-200" />
          <span className="h-3 animate-pulse rounded bg-neutral-100" style={{ width: `${w}%` }} />
        </li>
      ))}
    </ul>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-1.5 py-2 text-sm text-neutral-400">{children}</p>;
}
